import React from 'react';
import { toast } from 'sonner';
import { ExternalLink } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { useBreadcrumbs } from '@/client/hooks/use-breadcrumbs';
import { getApiErrorMessage } from '@/client/utils/errors';
import {
  PromotionInReview,
  useDecidePromotionReviewMutation,
  usePromotionReviewQuery,
} from '@/client/api/promotion-management/promotion-review';

const PromotionReviewPage: React.FC = () => {
  useBreadcrumbs([
    { label: 'Admin', href: '/admin' },
    { label: 'Promotions', href: '/admin/promotions' },
    { label: 'Review Queue', href: '/admin/promotions/review' },
  ]);

  const { data, isLoading, error } = usePromotionReviewQuery();
  const decide = useDecidePromotionReviewMutation();
  const rows = data?.data ?? [];

  const act = async (p: PromotionInReview, approve: boolean) => {
    let reason: string | undefined;
    if (!approve) {
      reason = window.prompt(`Why is "${p.name}" being rejected? This is kept in the audit trail.`)?.trim();
      if (!reason) return;
    }
    try {
      await decide.mutateAsync({ promid: p.promid, approve, reason });
      toast.success(approve ? 'Approved. It can serve now.' : 'Rejected. It stays held.');
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'Could not record the decision'));
    }
  };

  return (
    <div className="space-y-6 p-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Review Queue</h1>
        <p className="mt-1 max-w-3xl text-sm text-gray-600">
          Paid campaigns that link outside FeelyFeely. They do not serve until approved. A rejected
          campaign stays here, held, with the reason recorded.
        </p>
      </div>

      {error && (
        <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700">
          {getApiErrorMessage(error, 'Could not load the review queue')}
        </div>
      )}
      {isLoading && <Skeleton className="h-40 w-full" />}

      {!isLoading && !error && (
        <div className="rounded-xl border border-gray-200 bg-white shadow-sm">
          {rows.length === 0 ? (
            <p className="px-5 py-10 text-center text-sm text-gray-500">Nothing is waiting for review.</p>
          ) : (
            <ul className="divide-y divide-gray-100">
              {rows.map((p) => (
                <li key={p.promid} className="flex items-center gap-4 px-5 py-3">
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-medium text-gray-900">{p.name || `Promotion ${p.promid}`}</p>
                    {p.link_ext && (
                      <a
                        href={p.link_ext}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex max-w-full items-center gap-1 truncate text-xs text-blue-600 hover:underline"
                      >
                        <span className="truncate">{p.link_ext}</span>
                        <ExternalLink className="h-3 w-3 shrink-0" />
                      </a>
                    )}
                    <p className="text-xs text-gray-400">
                      #{p.promid}
                      {p.date_added && ` · submitted ${new Date(p.date_added).toLocaleDateString()}`}
                    </p>
                  </div>
                  <Button size="sm" disabled={decide.isPending} onClick={() => act(p, true)}>Approve</Button>
                  <Button size="sm" variant="outline" disabled={decide.isPending} onClick={() => act(p, false)}>Reject</Button>
                </li>
              ))}
            </ul>
          )}
          {data && data.total > rows.length && (
            <p className="border-t border-gray-100 px-5 py-3 text-xs text-gray-500">
              Showing the newest {rows.length} of {data.total}.
            </p>
          )}
        </div>
      )}
    </div>
  );
};

export default PromotionReviewPage;
